import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_UTM_HEADER,
  UTM_COOKIE_NAME,
  UTM_DEFAULT_TTL_DAYS,
} from '@/shared/config';

type UtmSet = {
  campaign: string;
  source?: string;
  medium?: string;
};

const PRM_REGEX = /_prm-h(\d+)(-.*)?$/;
const PROMO_BLOCK_REGEX = /^bl\d{2}$/;
const UTM_URL_PARAMS = ['utm_campaign', 'utm_source', 'utm_medium'];

const getCookie = (name: string) => {
  const row = document.cookie
    .split('; ')
    .find(item => item.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : null;
};

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${secure}`;
};

const parsePrmHours = (campaign: string): number | null => {
  const match = campaign.match(PRM_REGEX);
  if (!match) return null;
  const hours = Number(match[1]);
  return Number.isInteger(hours) && hours > 0 ? hours : null;
};

const parsePromoBlock = (campaign: string): string | null => {
  const block = campaign
    .match(PRM_REGEX)?.[2]
    ?.split('-')
    .find(param => PROMO_BLOCK_REGEX.test(param));
  return block ? block.slice(2) : null;
};

const getStoredUtm = (): UtmSet | null => {
  const raw = getCookie(UTM_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as UtmSet).campaign === 'string'
    ) {
      return parsed as UtmSet;
    }
    return null;
  } catch {
    return null;
  }
};

const captureUtmFromUrl = (search: string) => {
  const params = new URLSearchParams(search);
  const campaign = params.get('utm_campaign');
  if (!campaign) return;

  const stored = getStoredUtm();
  if (stored && parsePrmHours(stored.campaign) !== null) return;

  const utm: UtmSet = { campaign };
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  if (source) utm.source = source;
  if (medium) utm.medium = medium;

  const prmHours = parsePrmHours(campaign);
  const maxAgeSeconds =
    prmHours !== null
      ? prmHours * 60 * 60
      : UTM_DEFAULT_TTL_DAYS * 24 * 60 * 60;

  setCookie(UTM_COOKIE_NAME, JSON.stringify(utm), maxAgeSeconds);
};

const isIsolationActive = (search: string) => {
  const urlCampaign = new URLSearchParams(search).get('utm_campaign');
  if (urlCampaign && parsePrmHours(urlCampaign) !== null) return true;

  const stored = getStoredUtm();
  return stored !== null && parsePrmHours(stored.campaign) !== null;
};

const getPromoBlock = (search: string) => {
  const stored = getStoredUtm();
  const campaign =
    stored && parsePrmHours(stored.campaign) !== null
      ? stored.campaign
      : new URLSearchParams(search).get('utm_campaign');
  return campaign && parsePrmHours(campaign) !== null
    ? parsePromoBlock(campaign)
    : null;
};

const getUtmHeader = () => {
  const stored = getStoredUtm();
  if (!stored) return DEFAULT_UTM_HEADER;

  const params = new URLSearchParams();
  if (stored.source) params.set('utm_source', stored.source);
  if (stored.medium) params.set('utm_medium', stored.medium);
  params.set('utm_campaign', stored.campaign);
  return params.toString();
};

const useUtmTracking = () => {
  const { search, key } = useLocation();

  return useMemo(() => {
    captureUtmFromUrl(search);
    return {
      isIsolated: isIsolationActive(search),
      promoBlock: getPromoBlock(search),
    };
  }, [search, key]);
};

const useUtmUrlCleanup = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!UTM_URL_PARAMS.some(param => params.has(param))) return;

    UTM_URL_PARAMS.forEach(param => params.delete(param));
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true, state: location.state }
    );
  }, [location, navigate]);
};

export {
  parsePrmHours,
  parsePromoBlock,
  captureUtmFromUrl,
  getStoredUtm,
  isIsolationActive,
  getPromoBlock,
  getUtmHeader,
  useUtmTracking,
  useUtmUrlCleanup,
};
